import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useManualAlertEventData } from './useFetchManualAlerts';
import { getAlertsOption } from './manualAlertUtils';
import { alertEventSelector } from '@store/trends/alerts/selectors';
import { isEmpty } from 'lodash';
import { TIME_AXIS, VALUE_AXIS } from '@components/metrics-panel-hz/utils/constants';
import { findAxisIndex } from '../utils';
import { confirmAlertDrilldown } from '@components/alert-panel/ManualAlert/componentUtils';
import { DATA_TAGS } from '../useData';

// modifies input series
export function formatAlertSeries(option, data, selectedEvent = null) {
  const { events = [], alerts = {} } = data;

  // add alert series
  const alertSeries = getAlertsOption(
    { events, alerts, selectedEvent },
    {
      xAxisIndex: findAxisIndex(option.xAxis, TIME_AXIS),
      yAxisIndex: findAxisIndex(option.yAxis, VALUE_AXIS),
    },
  );

  return { series: [alertSeries] };
}

// there are two levels of enabled - hook enabled and feature toogle enabled
function useManualAlerts({
  instance,
  metric,
  enabled = false,
  additionalTriggerDeps = [],
  drilldown = true,
  dataTag = DATA_TAGS.manualAlertEvents,
}: any) {
  const dispatch = useDispatch();
  const selectedEvent = useSelector(alertEventSelector);
  const data = useManualAlertEventData({ metric, dataTag });

  // show selected event
  // even if alert markers are disabled/empty
  const alertData = useMemo(() => {
    if (isEmpty(data?.events) && !isEmpty(selectedEvent)) {
      const { alert, alertId } = selectedEvent;
      if (alert.metric === metric) {
        const alerts = { [alertId]: alert };
        const events = [{ ...selectedEvent }];
        return { alerts, events };
      }
    }
    return data;
  }, [data, metric, selectedEvent]);

  useEffect(() => {
    if (instance && enabled) {
      // replace merge workflow resolved the race issues
      // TODO: remove timeout post additional testing
      setTimeout(() => {
        const option = instance.getOption();
        if (option) {
          const { series } = formatAlertSeries(option, alertData, selectedEvent);
          instance.setOption({ series });
        }
      }, 100);
    }
  }, [
    instance,
    metric,
    enabled,
    JSON.stringify(alertData),
    selectedEvent,
    ...additionalTriggerDeps,
  ]);

  const handleAlertClick = useCallback(
    (params) => {
      if (params.componentType === 'markLine' && 'alert' in params.data) {
        confirmAlertDrilldown(dispatch, params.data);
      }
    },
    [metric],
  );

  useEffect(() => {
    if (instance && enabled && drilldown) {
      instance.on('click', handleAlertClick);
      return () => {
        instance.off('click', handleAlertClick);
      };
    }
  }, [instance, metric, enabled, drilldown]);
}

export default useManualAlerts;
